'use client'

import { useEffect, useRef } from 'react'

/**
 * Tracks the current state of user input modifier keys and right mouse button.
 * It is useful for:
 * - starting navigation progress bars
 * - knowing if a page will be opened in a new tab/window
 *
 * @example
 * const { alt, ctrl, meta, shift, rightMouse } = useUserInputModifiers().current
 */
export function useUserInputModifiers() {
  const modifiersRef = useRef({
    alt: false,
    meta: false, // ⌘ on Mac
    ctrl: false,
    shift: false,
    rightMouse: false,
  })

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey) modifiersRef.current.alt = true
      if (e.metaKey) modifiersRef.current.meta = true
      if (e.ctrlKey) modifiersRef.current.ctrl = true
      if (e.shiftKey) modifiersRef.current.shift = true
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      modifiersRef.current.alt = e.altKey
      modifiersRef.current.meta = e.metaKey
      modifiersRef.current.ctrl = e.ctrlKey
      modifiersRef.current.shift = e.shiftKey
    }

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 2) modifiersRef.current.rightMouse = true
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (e.button === 2) modifiersRef.current.rightMouse = false
    }

    const abortController = new AbortController()

    window.addEventListener('keydown', handleKeyDown, abortController)
    window.addEventListener('keyup', handleKeyUp, abortController)
    window.addEventListener('mousedown', handleMouseDown, abortController)
    window.addEventListener('mouseup', handleMouseUp, abortController)

    return () => {
      abortController.abort()
    }
  }, [])

  return modifiersRef
}
